import { useCallback, useEffect, useState } from 'react';
import { STRINGS } from '@maintenance-tracker/shared';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { api } from '@/lib/utils';

type SocialStatus = {
  honesty: string;
  earlyAccess: {
    id: string;
    details: Record<string, unknown>;
    withdrawnAt?: string | null;
    createdAt: string;
    updatedAt: string;
  } | null;
  audit: {
    id: string;
    status: string;
    publishedToOwnerAt: string;
    findings: Array<{
      id: string;
      platform: string;
      observation: string;
      evidenceUrl: string | null;
      recommendation: string | null;
      confidence: string | null;
      manuallyReviewed: boolean;
    }>;
  } | null;
};

const GOALS = [
  { id: 'look_professional', label: 'Look more professional' },
  { id: 'local_discovery', label: 'Improve local discovery' },
  { id: 'post_consistently', label: 'Post consistently' },
  { id: 'showcase', label: 'Showcase projects' },
  { id: 'understand_content', label: 'Understand what content works' },
  { id: 'more_inquiries', label: 'Get more inquiries' },
];

const PLATFORMS = [
  'Google Business Profile',
  'Facebook',
  'Instagram',
  'YouTube',
  'TikTok',
  'Nextdoor',
  'none',
];

export function SocialPage() {
  const [status, setStatus] = useState<SocialStatus | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [goals, setGoals] = useState<string[]>([]);
  const [howManaged, setHowManaged] = useState('');
  const [timeSpent, setTimeSpent] = useState('');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [platformUrls, setPlatformUrls] = useState<Record<string, string>>({});
  const [challenge, setChallenge] = useState('');
  const [pilotAcknowledged, setPilotAcknowledged] = useState(false);
  const [futureInterest, setFutureInterest] = useState(false);
  const [openText, setOpenText] = useState('');
  const [consentToContact, setConsentToContact] = useState(true);

  const load = useCallback(async () => {
    const d = await api<SocialStatus>('/api/v1/social/status');
    setStatus(d);
    const details = d.earlyAccess?.details;
    if (details && !d.earlyAccess?.withdrawnAt) {
      setGoals(Array.isArray(details.goals) ? (details.goals as string[]) : []);
      setHowManaged(typeof details.howManaged === 'string' ? details.howManaged : '');
      setTimeSpent(typeof details.timeSpent === 'string' ? details.timeSpent : '');
      setPlatforms(Array.isArray(details.platforms) ? (details.platforms as string[]) : []);
      setPlatformUrls(
        details.platformUrls && typeof details.platformUrls === 'object'
          ? (details.platformUrls as Record<string, string>)
          : {},
      );
      setChallenge(typeof details.challenge === 'string' ? details.challenge : '');
      setPilotAcknowledged(Boolean(details.pilotAcknowledged));
      setFutureInterest(Boolean(details.futureInterest));
      setOpenText(typeof details.openText === 'string' ? details.openText : '');
    }
  }, []);

  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);

  const requested = Boolean(status?.earlyAccess && !status.earlyAccess.withdrawnAt);

  const submit = async (withdraw = false) => {
    setBusy(true);
    setMsg('');
    try {
      const d = await api<{ message: string }>('/api/v1/early-access/social_media', {
        method: 'POST',
        body: JSON.stringify({
          goals,
          howManaged,
          timeSpent,
          platforms,
          platformUrls,
          challenge,
          pilotAcknowledged,
          futureInterest,
          openText,
          consentToContact,
          withdraw,
        }),
      });
      setMsg(d.message);
      setFormOpen(false);
      await load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <PageHeader title={STRINGS.social.title} description="Coming soon" />

      <p className="text-base text-text-secondary">{STRINGS.social.promise}</p>

      <section>
        <h3 className="font-semibold text-text-primary">Roadmap</h3>
        <ol className="mt-4 space-y-4 border-l border-border pl-4">
          <li>
            <p className="font-medium">Stage 1 — Audit and suggestions</p>
            <p className="text-sm text-text-secondary">
              We review your public Google Business Profile and social pages and tell you what to fix.
            </p>
          </li>
          <li>
            <p className="font-medium">Stage 2 — Content and analytics help</p>
            <p className="text-sm text-text-secondary">
              Complete content planning and management with simple performance summaries.
            </p>
          </li>
          <li>
            <p className="font-medium">Stage 3 — Smarter content creation</p>
            <p className="text-sm text-text-secondary">
              AI plus human-reviewed content ideas and drafts, with your approval before anything is
              posted.
            </p>
          </li>
        </ol>
      </section>

      <section>
        <h3 className="font-semibold text-text-primary">What you&apos;d get first</h3>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-text-secondary">
          <li>Checklist for your Google Business Profile</li>
          <li>Profile completeness and consistency check across platforms</li>
          <li>Photo and posting ideas for your trade</li>
          <li>Review-response suggestions</li>
        </ul>
      </section>

      {requested && status?.earlyAccess && (
        <section className="rounded-xl border border-border bg-bg-surface p-5">
          <p className="text-sm font-medium text-text-primary">Request received</p>
          <p className="mt-1 text-sm text-text-secondary">
            Submitted {new Date(status.earlyAccess.createdAt).toLocaleDateString()}. No waitlist
            position or launch date — we&apos;ll reach out when the first group opens.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => setFormOpen(true)}>
              Update my request
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void submit(true)}>
              Withdraw my request
            </Button>
          </div>
        </section>
      )}

      {status?.audit && (
        <section className="rounded-xl border border-border bg-bg-surface p-5 space-y-4">
          <div>
            <h3 className="font-semibold">Your private social audit</h3>
            <p className="mt-1 text-xs text-text-muted">
              Manually reviewed · published to your workspace only · not a public directory
            </p>
          </div>
          {status.audit.findings.slice(0, 3).map((f) => (
            <div key={f.id} className="border-t border-border pt-3">
              <p className="text-sm font-medium">
                {f.platform}: {f.observation}
              </p>
              {f.recommendation && (
                <p className="mt-1 text-sm text-text-secondary">{f.recommendation}</p>
              )}
              {f.evidenceUrl && (
                <a
                  href={f.evidenceUrl}
                  className="mt-1 inline-block text-xs text-brand hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  Evidence
                </a>
              )}
            </div>
          ))}
        </section>
      )}

      {!requested && !formOpen && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setFormOpen(true)}>Request early access</Button>
          <Button variant="secondary" onClick={() => setFormOpen(true)}>
            Tell us what you need
          </Button>
        </div>
      )}

      {formOpen && (
        <section className="space-y-4 rounded-xl border border-border bg-bg-surface p-5">
          <h3 className="font-semibold">Early access (under 2 minutes)</h3>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Main goal</legend>
            {GOALS.map((g) => (
              <label key={g.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={goals.includes(g.id)}
                  onChange={(e) =>
                    setGoals((prev) =>
                      e.target.checked ? [...prev, g.id] : prev.filter((x) => x !== g.id),
                    )
                  }
                />
                {g.label}
              </label>
            ))}
          </fieldset>
          <label className="block space-y-1 text-sm">
            <span className="font-medium">How you manage social</span>
            <Select value={howManaged} onChange={(e) => setHowManaged(e.target.value)}>
              <option value="">Select…</option>
              <option value="myself">Myself</option>
              <option value="employee_family">Employee / family</option>
              <option value="agency">Agency</option>
              <option value="rarely">Rarely post</option>
              <option value="other">Other</option>
            </Select>
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium">Time spent (optional)</span>
            <Input value={timeSpent} onChange={(e) => setTimeSpent(e.target.value)} />
          </label>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Platforms</legend>
            {PLATFORMS.map((p) => (
              <div key={p} className="space-y-1">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={platforms.includes(p)}
                    onChange={(e) =>
                      setPlatforms((prev) =>
                        e.target.checked ? [...prev, p] : prev.filter((x) => x !== p),
                      )
                    }
                  />
                  {p}
                </label>
                {platforms.includes(p) && p !== 'none' && (
                  <Input
                    placeholder="Optional URL"
                    value={platformUrls[p] ?? ''}
                    onChange={(e) =>
                      setPlatformUrls((prev) => ({ ...prev, [p]: e.target.value }))
                    }
                  />
                )}
              </div>
            ))}
          </fieldset>
          <label className="block space-y-1 text-sm">
            <span className="font-medium">Biggest challenge</span>
            <Select value={challenge} onChange={(e) => setChallenge(e.target.value)}>
              <option value="">Select…</option>
              <option value="no_time">No time</option>
              <option value="unsure_what_to_post">Not sure what to post</option>
              <option value="no_photos">No photos</option>
              <option value="low_engagement">Low engagement</option>
              <option value="other">Other</option>
            </Select>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={pilotAcknowledged}
              onChange={(e) => setPilotAcknowledged(e.target.checked)}
              className="mt-1"
            />
            <span>
              I understand this is a pilot: limited audit scope, manual review possible, no guaranteed
              results or slot.
            </span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={futureInterest}
              onChange={(e) => setFutureInterest(e.target.checked)}
            />
            Interested in future content/analytics testing
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium">What should we take off your plate?</span>
            <Input value={openText} onChange={(e) => setOpenText(e.target.value)} />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={consentToContact}
              onChange={(e) => setConsentToContact(e.target.checked)}
            />
            Contact me when early access opens
          </label>
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy || !pilotAcknowledged} onClick={() => void submit(false)}>
              {requested ? 'Update my request' : 'Submit'}
            </Button>
            <Button variant="ghost" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
          </div>
        </section>
      )}

      {msg && <p className="text-sm text-text-secondary">{msg}</p>}

      <p className="text-sm text-text-muted">{status?.honesty ?? STRINGS.earlyAccess.socialHonesty}</p>
      <p className="text-xs text-text-muted">
        No Connect Facebook, Generate posts, or View analytics until this is built. No fake metrics.
      </p>
    </div>
  );
}
